import {
  Component,
  ChangeDetectionStrategy,
  Injector,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom, throwError } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TranslateService } from '@ngx-translate/core';
import { DocumentsService, EditOrganisation } from '@src/api';
import { BusinessTypesService, OpfTypeService, OrganisationService } from '@src/core/services';
import { OrganisationUI, OrganisationUpsert, ViewMode } from '@src/models';
import { DialogConfirmComponent } from '@src/app/shared/dialogs';
import { isFileContact } from '@src/utils';
import { BaseResizableComponent } from '@src/app/modules/base';
import { PhotoService } from '@src/app/modules/photo';

import { OrganisationInfoStoreService, PermissionService } from '../services';

@Component({
  selector: 'app-organisation-info',
  templateUrl: './organisation-info.component.html',
  styleUrls: ['./organisation-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganisationInfoComponent extends BaseResizableComponent implements OnChanges {
  private readonly organisationService = inject(OrganisationService);
  private readonly photoService = inject(PhotoService);
  private readonly documentsService = inject(DocumentsService);
  private readonly opfTypeService = inject(OpfTypeService);
  private readonly businessTypesService = inject(BusinessTypesService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);
  private readonly organisationInfoStore = inject(OrganisationInfoStoreService);

  @Input() mode: ViewMode = 'view';
  @Input() organisationId?: string | null;
  @Input() parentOrganisationId?: string;
  @Output() saved = new EventEmitter<string>();
  @Output() canceled = new EventEmitter<void>();
  @Output() deleted = new EventEmitter<string>();

  data?: OrganisationUI;

  allowEditing$ = this.permissionService.allowEditing$;
  allowDeleting$ = this.permissionService.allowDeleting$;
  allowSpecialFieldsViewing$ = this.permissionService.allowSpecialFieldsViewing$;
  allowSpecialFieldsEditing$ = this.permissionService.allowSpecialFieldsEditing$;
  allowSpecialFieldsForAssociationViewing$ = this.permissionService.allowSpecialFieldsForAssociationViewing$;
  allowSpecialFieldsForAssociationEditing$ = this.permissionService.allowSpecialFieldsForAssociationEditing$;

  opfTypes$ = this.opfTypeService.opfTypes$;
  businessTypes$ = this.businessTypesService.businessTypes$;

  loading$ = new BehaviorSubject<boolean>(true);

  private readonly confirmCancelEditingDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('common.dialogs.undoEditHeader'),
      size: 's',
      closeable: false,
    },
  );
  private readonly confirmDeleteOrganisationDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('common.dialogs.deleteHeader'),
      size: 's',
      closeable: false,
    },
  );

  override ngOnInit(): void {
    super.ngOnInit();

    this.opfTypeService.getOPFTypes();
    this.businessTypesService.getBusinessTypes();

    this.organisationInfoStore.data$.subscribe(() => {
      if (this.organisationId) {
        this.data = this.organisationInfoStore.getData(this.organisationId);
        this.cdr.markForCheck();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.organisationId) {
      if (!!this.organisationId) {
        this.mode = 'view';
        this.organisationService.getOrganisationNew(this.organisationId).subscribe();
        this.permissionService.getPermissions(this.organisationId);
      }
    }

    if (changes.mode) {
      if (this.mode === 'create') {
        this.data = { parentOrganisation: this.parentOrganisationId };
        this.permissionService.getPermissions();
      }
    }

    this.cdr.markForCheck();
  }

  onStartEditing() {
    this.mode = 'edit';
  }

  async onSaveData(data: OrganisationUpsert): Promise<void> {
    this.loading$.next(true);
    let dataToSend = { ...data };
    if (this.parentOrganisationId) {
      dataToSend = { ...dataToSend, parentOrganisation: this.parentOrganisationId };
    }

    if (data.contacts) {
      const promises = data.contacts.map(async contact => {
        if (isFileContact(contact.contactTypeId)) {
          if (contact.oldDocument?.id) {
            contact.contact = contact.oldDocument.id;
          }

          if (contact.newDocument) {
            contact.contact = await this.uploadDocument(contact.newDocument, 10); // 10 Документ
          }

          contact.oldDocument = undefined;
          contact.newDocument = undefined;
        }
        return contact;
      });
      await Promise.all(promises);
    }

    if (this.mode === 'edit') {
      this.updateOrganisation(dataToSend);
    } else if (this.mode === 'create') {
      this.createOrganisation(dataToSend);
    }
  }

  onCancelEditing(): void {
    this.confirmCancelEditingDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: res => {
        if (res) {
          this.canceled.emit();
          if (this.organisationId) {
            this.data = this.organisationInfoStore.getData(this.organisationId);
            this.organisationService.getOrganisationNew(this.organisationId).subscribe();
          }

          this.mode = 'view';
        }
      },
    });
  }

  onDeleteOrganisation(): void {
    this.confirmDeleteOrganisationDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: res => {
        if (res) {
          if (this.organisationId) {
            lastValueFrom(this.organisationService.deleteOrganisation(this.organisationId)).then(() => {
              this.router.navigate(['association-organisations']);
              this.deleted.emit(this.organisationId!);
            });
          }
        }
      },
    });
  }

  private createOrganisation(data: OrganisationUpsert): void {
    this.organisationService
      .createOrganisation(data)
      .pipe(
        catchError(err => {
          this.loading$.next(false);
          return throwError(() => err);
        }),
        takeUntil(this.destroyed$$),
      )
      .subscribe(async res => {
        const newOrganisationId = res.id;

        if (data.photo && newOrganisationId) {
          await this.uploadPhoto(data.photo, newOrganisationId, true);
        }

        if (data.card && newOrganisationId) {
          await this.uploadDocument(data.card, 11, newOrganisationId); // 11 Карточка компании
        }

        if (data.invoice && newOrganisationId) {
          await this.uploadDocument(data.invoice, 15, newOrganisationId); // 15 Счет
        }

        this.saved.emit(newOrganisationId);
        this.mode = 'view';
        this.loading$.next(false);
        this.cdr.markForCheck();
      });
  }

  private updateOrganisation(data: OrganisationUpsert): void {
    this.organisationService
      .editOrganisation(data as EditOrganisation)
      .pipe(
        catchError(err => {
          this.loading$.next(false);
          return throwError(() => err);
        }),
        takeUntil(this.destroyed$$),
      )
      .subscribe(async () => {
        if (data.id) {
          if (data.photo) {
            await this.uploadPhoto(data.photo, data.id, true);
          }

          if (data.card) {
            await this.uploadDocument(data.card, 11, data.id); // 11 Карточка компании
          }

          if (data.invoice) {
            await this.uploadDocument(data.invoice, 15, data.id); // 15 Счет
          }

          if (data.deleteCard) {
            await this.deleteDocument(data.deleteCard);
          }

          if (data.deleteInvoice) {
            await this.deleteDocument(data.deleteInvoice);
          }

          this.organisationService.getOrganisationNew(data.id).subscribe();
          if (!data.parentOrganisation) {
            this.organisationService.getParentOrganisation(true);
          }

          this.saved.emit(data.id);
        }

        this.mode = 'view';
        this.loading$.next(false);
        this.cdr.markForCheck();
      });
  }

  private async uploadPhoto(photo: File, attachId: string, isCover: boolean) {
    if (!photo) return;

    return lastValueFrom(this.photoService.uploadPhoto(photo, attachId, isCover));
  }

  private async uploadDocument(document: File, documentType?: number, attachId?: string): Promise<string | undefined> {
    if (!document) return;

    const documentList = await lastValueFrom(
      this.documentsService.addDocuments([document], attachId, undefined, undefined, documentType),
    );

    return documentList?.files?.[0].id;
  }

  private async deleteDocument(documentId: string) {
    return await lastValueFrom(this.documentsService.deleteDocument(documentId));
  }
}
